import { Card } from "@/components/ui/card";
import { Wizard } from "@/components/ui/wizard/Wizard";
import { useBusinessOnboarding } from "@/hooks/useBusinessOnboarding";
import { businessOnboardingSteps } from "./businessOnboarding/BusinessOnboardingSteps";
import BusinessInfoStep from "./businessOnboarding/BusinessInfoStep";
import OnlinePresenceStep from "./businessOnboarding/OnlinePresenceStep";
import BrandVoiceStep from "./businessOnboarding/BrandVoiceStep";
import ReviewStep from "./businessOnboarding/ReviewStep";
import NavigationButtons from "./businessOnboarding/NavigationButtons";
import { useWebsiteAnalyzer } from "./businessOnboarding/WebsiteAnalyzer";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ValidationProvider } from "@/contexts/ValidationContext";

export const BusinessOnboarding = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  
  useEffect(() => {
    // Check if user is authenticated and get onboarding status
    const checkUserAndOnboardingStatus = async () => {
      try {
        setIsCheckingStatus(true);
        const { data } = await supabase.auth.getSession();
        
        if (!data.session) {
          toast({
            title: "Authentication required",
            description: "Please sign in to access the onboarding process.",
            variant: "destructive",
          });
          navigate("/auth");
          return;
        }
        
        // Check if user has already completed onboarding
        const { data: businessData, error } = await supabase
          .from('business')
          .select('is_onboarding_complete')
          .eq('owner_id', data.session.user.id)
          .maybeSingle();
        
        if (businessData) {
          if (businessData.is_onboarding_complete) {
            // User has already completed onboarding
            toast({
              title: "Already onboarded",
              description: "You have already completed the onboarding process.",
            });
            navigate("/app/dashboard");
            return;
          } else {
            // Business exists but onboarding is not complete
            // Allow continuing the onboarding process
            // Get the partial step data to determine where they left off
            // For now we're keeping it simple and starting from the beginning
          }
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      } finally {
        setIsCheckingStatus(false);
      }
    };
    
    checkUserAndOnboardingStatus();
  }, [navigate, toast]);

  if (isCheckingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking onboarding status...</p>
        </div>
      </div>
    );
  }

  return (
    <ValidationProvider>
      <BusinessOnboardingContent initialStep={currentStep} />
    </ValidationProvider>
  );
};

const BusinessOnboardingContent = ({ initialStep = 1 }) => {
  const {
    step,
    setStep,
    businessData,
    updateBusinessData,
    isSubmitting,
    handleSubmit,
    handleNext,
    handleBack
  } = useBusinessOnboarding();

  // Use our website analyzer hook
  const { analyzeWebsite, isAnalyzing, analysisError } = useWebsiteAnalyzer({
    businessData,
    updateBusinessData
  });

  // Set initial step if provided
  useEffect(() => {
    if (initialStep > 1) {
      setStep(initialStep);
    }
  }, [initialStep, setStep]);

  // Render the current step
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <BusinessInfoStep 
            businessData={businessData} 
            updateBusinessData={updateBusinessData} 
          />
        );
      case 2:
        return (
          <OnlinePresenceStep 
            businessData={businessData} 
            updateBusinessData={updateBusinessData}
            analyzeWebsite={analyzeWebsite}
            isAnalyzing={isAnalyzing}
            analysisError={analysisError}
          />
        );
      case 3:
        return (
          <BrandVoiceStep 
            brandVoice={businessData.brandVoice} 
            updateBusinessData={updateBusinessData}
          />
        );
      case 4:
        return <ReviewStep businessData={businessData} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8 animate-fade-in">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2">Set Up Your Marketing Assistant</h2>
        <p className="text-muted-foreground">
          Let's help VirtuStaff.ai understand your business to create personalized content
        </p>
      </div>
      
      <Wizard 
        steps={businessOnboardingSteps} 
        currentStep={step - 1}
        onChange={(newStep) => setStep(newStep + 1)}
        className="mb-6"
        wizardId="business-onboarding"
      />

      <Card className="glass-card p-6 transition-all duration-300">
        {renderStep()}
        
        <NavigationButtons
          step={step}
          totalSteps={businessOnboardingSteps.length}
          handleBack={handleBack}
          handleNext={handleNext}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </Card>
    </div>
  );
};

export default BusinessOnboarding;
